import React, { useState } from 'react';
import type { CreatorDeal } from '../../api/payment/brand-deal.api';
import BrandPostPreview from './brand-post-preview';
import ContentFormatBadge from './content-format-badge';
import { BrandThreadLink } from './brand-thread-panel';
import {
  DIRECT_EDIT_FAIRNESS_NOTE,
  DIRECT_EDIT_USED_UP_NOTE,
  EDIT_NOTE_MAX_LENGTH,
  EXISTING_POST_PENDING_NOTE,
  EXISTING_POST_PICKED_NOTE,
  contentVersionLabel,
  directEditCounterLabel,
  existingPostLink,
  validateEditNote,
} from '../../utils/brand-deal-content';

export interface BrandInviteCardProps {
  deal: CreatorDeal;
  authorName?: string | null;
  authorAvatar?: string | null;
  netLabel: string;
  respondByLabel: string;
  /** Đang có thao tác (đồng ý, từ chối, đề nghị chỉnh) trên lời mời này. */
  busy: boolean;
  onAccept: () => void;
  onDecline: () => void;
  /** Trả true khi gửi được để đóng ô ghi chú. */
  onRequestEdit: (note: string) => Promise<boolean>;
}

/**
 * Một lời mời đang chờ phía creator (spec 2026-10-12 §5): xem trước đúng bản nội dung mới nhất, số bản, "Đề nghị chỉnh x/n"
 * kèm ô ghi chú, Đồng ý hoặc Từ chối. Khai ở cấp module để ô ghi chú giữ chữ khi trang tải lại danh sách.
 */
const BrandInviteCard: React.FC<BrandInviteCardProps> = ({
  deal,
  authorName,
  authorAvatar,
  netLabel,
  respondByLabel,
  busy,
  onAccept,
  onDecline,
  onRequestEdit,
}) => {
  const [editing, setEditing] = useState(false);
  const [note, setNote] = useState('');
  const [noteError, setNoteError] = useState<string | null>(null);
  const used = deal.directEditCount ?? 0;
  const max = deal.directEditMax ?? 0;
  const unavailable = !!deal.existingPostUnavailable;
  const versionLabel = contentVersionLabel(deal.contentVersion);
  const noteId = `edit-note-${deal.id}`;

  const submitNote = async () => {
    const error = validateEditNote(note);
    setNoteError(error);
    if (error) return;
    if (await onRequestEdit(note.trim())) {
      setNote('');
      setEditing(false);
    }
  };

  return (
    <div className="ui-card flex flex-col gap-3 p-4">
      <BrandPostPreview
        authorName={authorName}
        authorAvatar={authorAvatar}
        title={deal.campaign.title}
        captionWithLink={deal.campaign.captionWithLink}
        images={deal.campaign.images}
        videos={deal.campaign.videos}
        brandName={deal.campaign.brandName}
        ordererKind={deal.campaign.ordererKind}
        // Lời mời chỉ tạo được từ tài khoản đặt đơn đã xác minh (bất biến phía máy chủ).
        brandVerified
      />
      {deal.existingPostId && (
        <p className="text-[13px] ui-text">
          {EXISTING_POST_PICKED_NOTE}{' '}
          <a href={existingPostLink(deal.existingPostId)} target="_blank" rel="noopener noreferrer" className="font-semibold text-[#0057FF]">
            Xem bài gốc
          </a>
        </p>
      )}
      {unavailable && (
        <p className="ui-divider rounded-lg border bg-amber-50 p-3 text-[13px] text-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
          {EXISTING_POST_PENDING_NOTE}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-[14px]">
        <ContentFormatBadge format={deal.campaign.contentFormat} />
        {versionLabel && <span className="ui-muted">{versionLabel}</span>}
        <div className="ui-text">
          Bạn nhận <span className="font-bold text-emerald-600 dark:text-emerald-400">{netLabel}</span>
        </div>
        <div className="ui-muted">Giữ bài {deal.campaign.holdDays} ngày</div>
        <div className="ui-muted">Hạn trả lời: {respondByLabel}</div>
        {max > 0 && <div className="font-semibold text-amber-600 dark:text-amber-400">{directEditCounterLabel(used, max)}</div>}
      </div>
      {editing && (
        <div className="ui-divider flex flex-col gap-2 rounded-lg border p-3">
          <label htmlFor={noteId} className="text-[13px] ui-text">
            Bạn muốn người đặt chỉnh gì?
          </label>
          <textarea
            id={noteId}
            rows={3}
            value={note}
            maxLength={EDIT_NOTE_MAX_LENGTH}
            onChange={(e) => setNote(e.target.value)}
            className="w-full rounded-md border ui-divider bg-transparent px-3 py-2 text-[14px] outline-none ui-text"
          />
          {noteError && <p className="text-[12px] text-rose-600 dark:text-rose-400">{noteError}</p>}
          <p className="text-[12px] ui-muted">{DIRECT_EDIT_FAIRNESS_NOTE}</p>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={submitNote}
              className="rounded-lg bg-[#0057FF] px-3 py-1.5 text-[13px] font-semibold text-white disabled:opacity-60"
            >
              Gửi đề nghị
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => setEditing(false)}
              className="rounded-lg border ui-divider px-3 py-1.5 text-[13px] ui-text"
            >
              Huỷ
            </button>
          </div>
        </div>
      )}
      {!deal.canRequestEdit && max > 0 && used >= max && <p className="text-[12px] ui-muted">{DIRECT_EDIT_USED_UP_NOTE}</p>}
      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          disabled={busy}
          onClick={onDecline}
          className="rounded-lg border ui-divider px-4 py-2 text-[14px] font-semibold ui-text disabled:opacity-60"
        >
          Từ chối
        </button>
        {deal.canRequestEdit && !editing && (
          <button
            type="button"
            disabled={busy}
            onClick={() => setEditing(true)}
            className="rounded-lg border border-[#0057FF] px-4 py-2 text-[14px] font-semibold text-[#0057FF] disabled:opacity-60"
          >
            Đề nghị chỉnh
          </button>
        )}
        <button
          type="button"
          disabled={busy || unavailable}
          onClick={onAccept}
          className="rounded-lg bg-[#0057FF] px-4 py-2 text-[14px] font-semibold text-white disabled:opacity-60"
        >
          {deal.existingPostId ? 'Đồng ý & Gắn nhãn' : 'Đồng ý & Đăng ngay'}
        </button>
        <span className="self-center text-[14px]">
          <BrandThreadLink dealId={deal.id} threadId={deal.threadId} />
        </span>
      </div>
    </div>
  );
};

export default BrandInviteCard;
